import { Box, Skeleton, Typography } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import { SprengnetterValuationHubProsaWebModelResponseProsaWebResponseRead as ProsaWebResponse } from "@/api/generatedApi";
import {
  getProsaWebDisplayFactor,
  getProsaWebIndicativeAdjustedValue,
  getProsaWebIndicativeFactor,
  getProsaWebIndicativePreliminaryValue,
  getProsaWebMarketValue,
} from "@/helpers/prosaWeb";
import { ObjectTypeEnum } from "@/types/refineRealEstate";

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createStyles = (theme: Theme) => ({
  root: {
    display: "grid",
    gap: "20px",
  },
  title: {
    color: theme.semanticColors.text.primary,
    fontSize: "16px",
    lineHeight: "27px",
    fontWeight: 700,
  },
  card: {
    display: "grid",
    gap: "20px",
    minWidth: "280px",
    padding: theme.analysisLayout.cardPadding,
    borderLeft: `2px solid ${theme.semanticColors.border.action}`,
  },
  rows: {
    display: "grid",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    alignItems: "center",
    gap: "16px",
    padding: "8px",
    borderBottom: `1px solid ${theme.semanticColors.border.subtle}`,
  },
  highlightedRow: {
    backgroundColor: theme.semanticColors.surface.blueBg,
  },
  labelColumn: {
    display: "grid",
    gap: "4px",
    minWidth: 0,
  },
  label: {
    color: theme.semanticColors.text.primary,
  },
  hint: {
    color: theme.semanticColors.text.muted,
    fontSize: "15px",
    lineHeight: "23px",
    fontWeight: 400,
  },
  compactValue: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    minHeight: "30px",
    color: theme.semanticColors.text.primary,
    fontSize: "16px",
    lineHeight: "24px",
    fontWeight: 400,
    whiteSpace: "nowrap",
  },
  highlightedValue: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "100px",
    minHeight: "24px",
    color: theme.semanticColors.text.primary,
    fontSize: "16px",
    lineHeight: "24px",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  blurredValue: {
    filter: "blur(4px)",
    userSelect: "none",
  },
});

interface BreakdownRow {
  key: string;
  label: string;
  description?: string;
  blurDescription?: boolean;
  value: string;
  valuePrefix?: string;
  rounded?: boolean;
  highlighted?: boolean;
}

interface IndicativeValuationBreakdownProps {
  valuation: ProsaWebResponse | null;
  objectType?: ObjectTypeEnum;
  factorValue?: number;
  subtotalValue?: number | null;
  livingArea?: number | null;
  plotArea?: number | null;
  isLoading?: boolean;
  blurValues?: boolean;
}

type BreakdownTitleKey =
  | "breakdown.apartment.title"
  | "breakdown.house.title"
  | "breakdown.income.title"
  | "breakdown.plot.title";

const getBreakdownTitleKey = (objectType?: ObjectTypeEnum): BreakdownTitleKey => {
  switch (objectType) {
    case ObjectTypeEnum.apartment:
      return "breakdown.apartment.title";
    case ObjectTypeEnum.multiFamilyHouse:
    case ObjectTypeEnum.mixedUseProperty:
      return "breakdown.income.title";
    case ObjectTypeEnum.plotOfLand:
      return "breakdown.plot.title";
    default:
      return "breakdown.house.title";
  }
};

const formatEuro = (value?: number | null, options?: { roundToThousands?: boolean }): string => {
  if (value == null) {
    return "—";
  }

  const normalizedValue = options?.roundToThousands ? Math.round(value / 1000) * 1000 : Math.round(value);

  return normalizedValue.toLocaleString("de-DE") + " €";
};

const formatUnitValue = (value: number | null | undefined, unit: string): string => {
  if (value == null) {
    return "—";
  }

  return value.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " " + unit;
};

const formatPercentage = (value?: number | null): string => {
  if (value == null) {
    return "-";
  }

  return value.toLocaleString("de-DE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const formatFactorValue = (value?: number | null, options?: { isApartment?: boolean }): string => {
  if (value == null) {
    return "-";
  }

  if (options?.isApartment) {
    return Math.round(value).toLocaleString("de-DE");
  }

  return value.toLocaleString("de-DE");
};

const BreakdownValue = ({
  value,
  valuePrefix,
  rounded,
  highlighted = false,
  isLoading = false,
  isBlurred = false,
}: Pick<BreakdownRow, "value" | "valuePrefix" | "rounded" | "highlighted"> & {
  isLoading?: boolean;
  isBlurred?: boolean;
}): JSX.Element => {
  const theme = useTheme();
  const styles = createStyles(theme);

  if (!highlighted) {
    return (
      <Box sx={{ ...styles.compactValue, ...(isBlurred ? styles.blurredValue : {}) }}>
        {isLoading ? (
          <Skeleton animation="wave" variant="rounded" width={100} height={20} />
        ) : valuePrefix ? (
          valuePrefix + " " + value
        ) : (
          value
        )}
      </Box>
    );
  }

  return (
    <Box sx={styles.highlightedValue}>
      <Typography component="span" sx={{ fontWeight: 600 }}>
        {rounded ? "rd." : ""}
      </Typography>
      {isLoading ? (
        <Skeleton
          animation="wave"
          variant="rounded"
          width={100}
          height={20}
          sx={isBlurred ? styles.blurredValue : undefined}
        />
      ) : (
        <Typography component="span" sx={{ fontWeight: 600, ...(isBlurred ? styles.blurredValue : {}) }}>
          {value}
        </Typography>
      )}
    </Box>
  );
};

export const IndicativeValuationBreakdown = ({
  objectType,
  valuation,
  factorValue,
  subtotalValue,
  livingArea,
  plotArea,
  isLoading = false,
  blurValues = false,
}: IndicativeValuationBreakdownProps): JSX.Element => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const { t } = useTranslation("IndicativeMarketValuationPanel");
  const isApartment = objectType === ObjectTypeEnum.apartment;
  const isPlot = objectType === ObjectTypeEnum.plotOfLand;
  const isMixedUseProperty = objectType === ObjectTypeEnum.mixedUseProperty;
  const breakdownTitleKey = getBreakdownTitleKey(objectType);
  const displayFactorValue = getProsaWebDisplayFactor(valuation, objectType)?.value;
  const apiFactorValue = getProsaWebIndicativeFactor(valuation, objectType)?.value;
  const resolvedFactorValue = factorValue ?? apiFactorValue;
  const currentMarketValue = getProsaWebMarketValue(valuation, {
    rounded: false,
    objectType,
    livingArea,
  });
  const preliminaryComparisonValue = getProsaWebIndicativePreliminaryValue(valuation, objectType);
  const resolvedComparisonValue = isApartment
    ? subtotalValue ?? currentMarketValue ?? getProsaWebIndicativeAdjustedValue(valuation, objectType)
    : subtotalValue ?? getProsaWebIndicativeAdjustedValue(valuation, objectType) ?? currentMarketValue;
  const isIncomeApproach = objectType === ObjectTypeEnum.multiFamilyHouse || isMixedUseProperty;
  const standardGroundValue =
    valuation?.factors?.standard_ground_value?.value ??
    valuation?.comparable_sales_approach?.standard_ground_value?.value;
  const calculatedLandValue = plotArea != null && standardGroundValue != null ? plotArea * standardGroundValue : null;
  const incomeApproach = valuation?.income_approach;
  const grossAnnualIncome = incomeApproach?.gross_annual_income;
  const mixedUseIncomeRows: BreakdownRow[] = isMixedUseProperty
    ? [
        {
          key: "commercialGrossAnnualIncome",
          label: t("breakdown.income.commercialPart"),
          description: t("breakdown.income.shareOfGrossIncome", {
            value: formatPercentage(incomeApproach?.commercial?.gross_annual_income_percentage),
          }),
          blurDescription: true,
          value: formatEuro(incomeApproach?.commercial?.gross_annual_income),
          valuePrefix: "+",
        },
        {
          key: "residentialGrossAnnualIncome",
          label: t("breakdown.income.residentialPart"),
          description: t("breakdown.income.shareOfGrossIncome", {
            value: formatPercentage(incomeApproach?.residential?.gross_annual_income_percentage),
          }),
          blurDescription: true,
          value: formatEuro(incomeApproach?.residential?.gross_annual_income),
          valuePrefix: "+",
        },
      ]
    : [];
  const rows: BreakdownRow[] = isIncomeApproach
    ? [
        ...mixedUseIncomeRows,
        {
          key: "grossAnnualIncome",
          label: t("breakdown.income.grossAnnualIncome"),
          value: formatEuro(grossAnnualIncome),
          highlighted: true,
        },
        {
          key: "propertyOperatingCosts",
          label: t("breakdown.income.propertyOperatingCosts.label"),
          description: t("breakdown.income.propertyOperatingCosts.description", {
            value: formatPercentage(incomeApproach?.operating_costs_percentage),
          }),
          blurDescription: true,
          value: formatEuro(incomeApproach?.property_operating_costs),
          valuePrefix: "-",
        },
        {
          key: "netAnnualIncome",
          label: t("breakdown.income.netAnnualIncome"),
          value: formatEuro(incomeApproach?.net_annual_income),
          highlighted: true,
        },
        {
          key: "landValueInterest",
          label: t("breakdown.income.landValueInterest.label"),
          description: t("breakdown.income.landValueInterest.description"),
          value: formatEuro(incomeApproach?.land_value_interest),
          valuePrefix: "-",
        },
        {
          key: "structuralNetIncome",
          label: t("breakdown.income.structuralNetIncome"),
          value: formatEuro(incomeApproach?.net_building_annual_income),
          highlighted: true,
        },
        {
          key: "capitalization",
          label: t("breakdown.income.capitalization.label"),
          description: t("breakdown.income.capitalization.description"),
          value: formatFactorValue(incomeApproach?.capitalization_multiplier ?? incomeApproach?.capitalized_value),
          valuePrefix: "×",
        },
        {
          key: "preliminaryStructuralIncomeValue",
          label: t("breakdown.income.preliminaryStructuralIncomeValue"),
          value: formatEuro(incomeApproach?.preliminary_buildings_value),
          highlighted: true,
        },
        {
          key: "landValue",
          label: t("breakdown.income.landValue.label"),
          description: t("breakdown.income.landValue.description"),
          value: formatEuro(incomeApproach?.land_value),
          valuePrefix: "+",
        },
        {
          key: "preliminaryIncomeValue",
          label: t("breakdown.income.preliminaryIncomeValue"),
          value: formatEuro(incomeApproach?.preliminary_value),
          highlighted: true,
        },
        {
          key: "marketAdjustment",
          label: t("breakdown.income.marketAdjustment"),
          value: formatEuro(incomeApproach?.property_specific_features_value),
          valuePrefix: "+",
        },
        {
          key: "resultingIncomeValue",
          label: t("breakdown.income.resultingIncomeValue"),
          value: formatEuro(valuation?.market_value?.rounded_value, { roundToThousands: true }),
          rounded: true,
          highlighted: true,
        },
      ]
    : isPlot
      ? [
          {
            key: "plotArea",
            label: t("breakdown.plot.plotArea"),
            value: formatUnitValue(plotArea, "m²"),
          },
          {
            key: "standardGroundValue",
            label: t("breakdown.plot.standardGroundValue.label"),
            description: t("breakdown.plot.standardGroundValue.description"),
            value: formatUnitValue(standardGroundValue, "€/m²"),
            valuePrefix: "x",
          },
          {
            key: "roundedLandValue",
            label: t("breakdown.plot.landValue"),
            value: formatEuro(
              valuation?.market_value?.rounded_value ?? subtotalValue ?? calculatedLandValue ?? currentMarketValue,
              { roundToThousands: true },
            ),
            rounded: true,
            highlighted: true,
          },
        ]
      : isApartment
        ? [
            {
              key: "preliminaryComparisonValue",
              label: t("breakdown.apartment.preliminaryComparisonValue"),
              value: formatEuro(preliminaryComparisonValue),
              highlighted: true,
            },
            {
              key: "comparisonFactor",
              label: t("breakdown.apartment.comparisonFactor.label"),
              description: t("breakdown.apartment.comparisonFactor.description"),
              value: formatFactorValue(factorValue ?? displayFactorValue ?? resolvedFactorValue, { isApartment: true }),
            },
            {
              key: "comparisonValue",
              label: t("breakdown.apartment.comparisonValue"),
              value: formatEuro(resolvedComparisonValue, { roundToThousands: true }),
              rounded: true,
              highlighted: true,
            },
          ]
        : [
            {
              key: "buildingValueOfStructures",
              label: t("breakdown.house.buildingValueOfStructures.label"),
              description: t("breakdown.house.buildingValueOfStructures.description"),
              value: formatEuro(valuation?.cost_approach?.buildings_asset_value),
            },
            {
              key: "outdoorFacilities",
              label: t("breakdown.house.outdoorFacilities.label"),
              description: t("breakdown.house.outdoorFacilities.description"),
              value: formatEuro(valuation?.cost_approach?.exterior_facilities_value),
            },
            {
              key: "landValue",
              label: t("breakdown.house.landValue.label"),
              description: t("breakdown.house.landValue.description"),
              value:
                plotArea && valuation?.factors?.standard_ground_value?.value
                  ? formatEuro(valuation?.factors?.standard_ground_value?.value * plotArea)
                  : "-",
            },
            {
              key: "preliminaryMaterialValue",
              label: t("breakdown.house.preliminaryMaterialValue"),
              value: formatEuro(valuation?.cost_approach?.preliminary_value),
              highlighted: true,
            },
            {
              key: "materialValueFactor",
              label: t("breakdown.house.materialValueFactor.label"),
              description: t("breakdown.house.materialValueFactor.description"),
              value: resolvedFactorValue?.toLocaleString("de-DE") ?? "-",
            },
            {
              key: "materialValue",
              label: t("breakdown.house.materialValue"),
              value: formatEuro(subtotalValue, { roundToThousands: true }),
              rounded: true,
              highlighted: true,
            },
          ];

  return (
    <Box sx={styles.root}>
      <Typography sx={styles.title}>{t(breakdownTitleKey)}</Typography>
      <Box sx={styles.card}>
        <Box sx={styles.rows}>
          {rows.map((row) => (
            <Box
              key={row.key}
              data-cy={`indicative-market-value-breakdown-${row.key}`}
              sx={{
                ...styles.row,
                ...(row.highlighted ? styles.highlightedRow : {}),
              }}
            >
              <Box sx={styles.labelColumn}>
                <Typography variant={"body1"} sx={{ fontWeight: row.highlighted ? 600 : 400 }}>
                  {row.label}
                </Typography>
                {row.description ? (
                  <Typography
                    sx={{
                      ...styles.hint,
                      ...(blurValues && row.blurDescription ? styles.blurredValue : {}),
                    }}
                  >
                    {row.description}
                  </Typography>
                ) : null}
              </Box>
              <BreakdownValue
                value={row.value}
                valuePrefix={row.valuePrefix}
                rounded={row.rounded}
                highlighted={row.highlighted}
                isLoading={isLoading}
                isBlurred={blurValues}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};
